//@ts-nocheck
import { parse } from 'graphql'

export interface DataDumpConfig {
  columns: { [key: string]: string }
  dwQuery: string
  gqlQuery?: string
}

class DataDumpConfigValidator {
  logger: any

  constructor(logger: any) {
    this.logger = logger
  }

  validate(config: DataDumpConfig): void {
    const { columns, dwQuery, gqlQuery } = config
    const metricFields = this._assembleMetricFields(dwQuery)
    const joinFields = gqlQuery
      ? this._assembleGraphQLQueryFields(gqlQuery)
      : new Set()
    for (const [key, value] of Object.entries(columns)) {
      if (value.startsWith('metric.')) {
        const parts = value.split('metric.')
        if (metricFields.has(parts[1])) {
          this.logger.debug(`${key} has matching ${value} in dwQuery`)
        } else {
          this.logger.error(
            `${key} missing ${value} in dwQuery`,
            Array.from(metricFields).join()
          )
        }
      } else if (value.includes('.')) {
        // Here we deal w/ array structures in a slightly hacky way,
        // we just remove any array references to match the gql parsed set.
        const cleaned = value.replace(/\[\d+\]/g, '')
        const parts = cleaned.split('.')
        // hack to account for unstructured data,
        // NOTE: this could conflict w/ stuff like PWDs
        const pIndex = parts.findIndex((p) => p === 'parameters')
        if (pIndex > 0) {
          const truncated = parts.slice(0, pIndex + 1).join('.')
          if (joinFields.has(truncated)) {
            this.logger.debug(
              `${key} matches truncated ${truncated} in gqlQuery`
            )
          } else {
            this.logger.error(
              `${key} missing ${value} in gqlQuery`,
              Array.from(joinFields).join()
            )
          }
        } else {
          if (joinFields.has(cleaned)) {
            this.logger.debug(`${key} has matching ${value} in gqlQuery`)
          } else {
            this.logger.error(
              `${key} missing ${value} in gqlQuery`,
              Array.from(joinFields).join()
            )
          }
        }
      } else {
        this.logger.debug(`${key} assigned static value: ${value}`)
      }
    }
  }

  _assembleGraphQLQueryFields(query: string): Set<unknown> {
    const ast = parse(query)
    const fields = new Set()

    const traverse = (selectionSet, prefix = '') => {
      for (const selection of selectionSet.selections) {
        if (selection.kind === 'Field') {
          const field = selection.name.value
          const path = prefix ? `${prefix}.${field}` : field
          if (selection.selectionSet) {
            traverse(selection.selectionSet, path)
          } else {
            fields.add(path)
          }
        }
      }
    }

    for (const def of ast.definitions) {
      // @ts-ignore
      if (def.selectionSet) {
        // @ts-ignore
        traverse(def.selectionSet)
      }
    }
    return fields
  }

  _assembleMetricFields(query: string): Set<unknown> {
    const fields = new Set()
    const selects = query.split(/select/i)
    for (const s of selects) {
      if (s) {
        const selections = s.split(/from/i)
        // This looks for lines that do contain one of:
        // - the end of a sql case statement, ie: END AS <name>
        // - a sql function, ie: to_char("timestamp", 'YYYY-MM-DD'),
        // both of which are expected to include an AS <name> clause.
        const regex1 = /^\s*,?\s*((end as \w+)|(["'\w ]+\(.*?\) as \w+))/gim
        const funcMatches = selections[0].match(regex1)
        if (funcMatches?.length) {
          for (const item of funcMatches) {
            const parts = item.split(/as/i)
            const identifier = parts[parts.length - 1]
            fields.add(identifier.trim())
          }
        }
        // This looks for lines that do not contain:
        // - any part of a sql case statement including CASE, WHEN, ELSE, and END
        // - a sql function
        // In this case, the lines are expected to contain a single field name,
        // optionally including an AS <name> clause.
        // A line can also include a preceding or succeeding comma.
        const regex2 =
          /^(?!(\s*,?\s*((case)|(when )|(else )|(end as \w+)|(["'\w ]+\(.*?\) as \w+))))\s*,?\s*["'\w ]+,?/gim
        const matches = selections[0].match(regex2)
        if (matches?.length) {
          for (const item of matches) {
            const parts = item.split(/as/i)
            const subparts = parts[parts.length - 1].split(',').map((s) => {
              const trimmed = s.trim()
              // special cases for sql reserved words like content and timestamp
              if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
                return trimmed.substring(1, trimmed.length - 1)
              }
              return trimmed
            })
            for (const p of subparts) {
              if (p) {
                fields.add(p)
              }
            }
          }
        }
      }
    }
    return fields
  }
}

export default DataDumpConfigValidator
